import { useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  Info,
  Loader2,
  Lock,
  PlugZap,
  Snowflake,
  TriangleAlert,
} from 'lucide-react';
import { Button } from '@/components/Button';
import { Card, CardBody, CardHeader } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { Skeleton, SkeletonRegion } from '@/components/Skeleton';
import { cn } from '@/lib/cn';
import { formatarDataHora, formatarMinutos, mensagemDeErro } from '@/lib/format';
import {
  congelamentoDoDia,
  congelamentosDoDia,
  deslocarMes,
  ehHoje,
  intervaloDe,
  mesmoMes,
  noDia,
  periodoVisivel,
  posicaoNaFaixa,
  rotuloDeHorario,
  semanasDoMes,
  type Periodo,
} from '@/features/changes/calendario';
import {
  chavesDeMudanca,
  obterCalendario,
  obterJanela,
  obterMudanca,
} from '@/features/changes/changes.api';
import { AvisoDeConflito, EtiquetaDeJanela } from '@/features/changes/componentes';
import {
  contarPorMudanca,
  mudancasBloqueadas,
  reunirConflitos,
  type MudancaAvaliada,
  type PanoramaDeConflitos,
} from '@/features/changes/conflitos';
import { LinkDaMudanca } from '@/features/changes/navegacao';
import {
  ROTULO_DE_TIPO_DE_CONFLITO,
  type Calendario,
  type Congelamento,
  type JanelaDetalhada,
  type JanelaListada,
} from '@/features/changes/tipos';

/**
 * O calendário de mudanças — a tela que o Comitê de Mudanças abre na reunião.
 *
 * ## O que ela precisa responder, nesta ordem
 *
 *   1. **o ambiente está congelado?** É a primeira pergunta porque muda todas
 *      as outras. Por isso o congelamento não é um item numa lista: ele hachura
 *      o dia inteiro, na grade e na faixa do período;
 *   2. **o que sobe, e quando?** As janelas, com as mudanças dentro de cada uma;
 *   3. **o que colide com o quê?** Duas mudanças no mesmo item de configuração
 *      em janelas que se sobrepõem. O servidor devolve isso como **informação**
 *      — nunca como erro, porque é o que uma versão de entrega faz de propósito
 *      — e é esta tela que a transforma em decisão;
 *   4. **o que o cliente vai sentir?** A Interrupção Projetada de Serviço.
 *
 * ## Por que a colisão é reunida no cliente
 *
 * Não existe rota agregada de conflito: o servidor calcula a colisão **por
 * mudança**, em `GET /tickets/:id/change`, do ponto de vista daquela mudança.
 * Reunir os pares é trabalho da tela, e mora em `conflitos.ts`, testado à
 * parte.
 *
 * O custo é uma requisição por janela ocupada mais uma por mudança agendada no
 * período. Num mês real isso é uma dúzia de chamadas curtas, e as duas ondas
 * correm em paralelo — mas é um custo, e é por isso que a análise só olha o
 * período visível: aumentar o recorte aumenta o número de chamadas.
 */
export function CalendarioPage() {
  const [referencia, setReferencia] = useState(() => new Date());
  /**
   * O período consultado é o que a grade **desenha**, e não o mês.
   *
   * A grade abre no domingo anterior ao dia 1º e fecha no sábado posterior ao
   * último dia: até doze dias visíveis caem fora de `mesDe`. Consultar pelo mês
   * fazia esses dias aparecerem limpos — sem hachura, sem janela —, e num
   * calendário cuja primeira pergunta é "o ambiente está congelado?" mostrar
   * dia congelado como livre é o erro que importa.
   */
  const periodo = useMemo(() => periodoVisivel(referencia), [referencia]);

  const calendario = useQuery({
    queryKey: chavesDeMudanca.calendario(periodo.inicio.toISOString(), periodo.fim.toISOString()),
    queryFn: () => obterCalendario(periodo.inicio, periodo.fim),
    // O calendário muda em ritmo de planejamento, não de operação: revalidar a
    // cada montagem faria uma requisição por navegação para um dado que passa a
    // semana igual.
    staleTime: 60_000,
  });

  const janelasOcupadas = (calendario.data?.windows ?? []).filter(
    (janela) => janela._count.changes > 0,
  );

  const detalhesDasJanelas = useQueries({
    queries: janelasOcupadas.map((janela) => ({
      queryKey: chavesDeMudanca.janela(janela.id),
      queryFn: () => obterJanela(janela.id),
      staleTime: 60_000,
    })),
  });

  const agendadas = detalhesDasJanelas.flatMap((consulta) =>
    (consulta.data?.changes ?? []).map((membro) => ({
      ticketId: membro.ticketId,
      janela: consulta.data as JanelaDetalhada,
      membro,
    })),
  );

  const mudancas = useQueries({
    queries: agendadas.map((linha) => ({
      queryKey: chavesDeMudanca.mudanca(linha.ticketId),
      queryFn: () => obterMudanca(linha.ticketId),
      staleTime: 60_000,
    })),
  });

  const avaliadas: MudancaAvaliada[] = mudancas.flatMap((consulta) => {
    const mudanca = consulta.data;
    if (mudanca === undefined) return [];
    return [
      {
        ticketId: mudanca.ticketId,
        numero: mudanca.ticket.number,
        assunto: mudanca.ticket.subject,
        janelaId: mudanca.changeWindowId,
        janelaNome: mudanca.changeWindow?.name ?? null,
        conflitos: mudanca.conflicts,
      },
    ];
  });

  // Sem memorização de propósito: `useQueries` devolve um array novo a cada
  // renderização, então uma dependência por identidade não memorizaria nada — e
  // uma dependência por conteúdo exigiria serializar a lista, que custa o mesmo
  // que refazer a conta. `reunirConflitos` é linear sobre dezenas de itens.
  const panorama = reunirConflitos(avaliadas);
  const conflitosPorMudanca = contarPorMudanca(panorama);
  const travadas = mudancasBloqueadas(panorama);

  const analisando =
    detalhesDasJanelas.some((c) => c.isPending) || mudancas.some((c) => c.isPending);

  if (calendario.isPending) {
    return (
      <SkeletonRegion label="Carregando o calendário de mudanças" className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-96 w-full" />
      </SkeletonRegion>
    );
  }

  if (calendario.isError) {
    return (
      <Card>
        <CardBody className="space-y-3 py-10 text-center">
          <p role="alert" className="text-sm text-destructive">
            {mensagemDeErro(calendario.error)}
          </p>
          <Button variant="secondary" size="sm" onClick={() => void calendario.refetch()}>
            Tentar de novo
          </Button>
        </CardBody>
      </Card>
    );
  }

  const dados = calendario.data;

  return (
    <div className="space-y-6">
      <BarraDoPeriodo
        referencia={referencia}
        aoTrocar={setReferencia}
        dados={dados}
        analisando={analisando}
      />

      <FaixaDeCongelamentos periodo={periodo} congelamentos={dados.blackouts} />

      <Grade
        referencia={referencia}
        janelas={dados.windows}
        congelamentos={dados.blackouts}
        mudancasPorJanela={agruparPorJanela(detalhesDasJanelas.map((c) => c.data))}
        conflitosPorMudanca={conflitosPorMudanca}
        travadas={travadas}
      />

      <PainelDeConflitos panorama={panorama} analisando={analisando} />

      <PainelDePso interrupcoes={dados.projectedOutages} />
    </div>
  );
}

/** `windowId → mudanças dentro dela`, a partir do que a onda 1 trouxe. */
function agruparPorJanela(
  detalhes: ReadonlyArray<JanelaDetalhada | undefined>,
): Map<string, JanelaDetalhada['changes']> {
  const mapa = new Map<string, JanelaDetalhada['changes']>();
  for (const janela of detalhes) {
    if (janela === undefined) continue;
    mapa.set(janela.id, janela.changes);
  }
  return mapa;
}

// ── barra do período ───────────────────────────────────────────────────────

const MES = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' });

function BarraDoPeriodo({
  referencia,
  aoTrocar,
  dados,
  analisando,
}: {
  referencia: Date;
  aoTrocar: (data: Date) => void;
  dados: Calendario;
  analisando: boolean;
}) {
  const bloqueantes = dados.blackouts.filter((b) => b.active && b.enforcement === 'BLOCKING');
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          aria-label="Mês anterior"
          onClick={() => aoTrocar(deslocarMes(referencia, -1))}
          icon={<ChevronLeft className="h-4 w-4" aria-hidden />}
        />
        {/* `first-letter:uppercase` e não `capitalize`: o formatador devolve
            "agosto de 2026", e `capitalize` produziria "Agosto De 2026" — em
            português a preposição não leva maiúscula. */}
        <h2 className="min-w-52 text-center text-lg font-semibold tracking-tight first-letter:uppercase">
          {MES.format(referencia)}
        </h2>
        <Button
          variant="ghost"
          size="sm"
          aria-label="Próximo mês"
          onClick={() => aoTrocar(deslocarMes(referencia, 1))}
          icon={<ChevronRight className="h-4 w-4" aria-hidden />}
        />
        <Button variant="secondary" size="sm" onClick={() => aoTrocar(new Date())}>
          Hoje
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <CalendarClock className="h-3.5 w-3.5" aria-hidden />
          {dados.windows.length} janela(s)
        </span>
        <span
          className={cn(
            'inline-flex items-center gap-1.5',
            bloqueantes.length > 0 && 'font-semibold text-destructive',
          )}
        >
          <Snowflake className="h-3.5 w-3.5" aria-hidden />
          {bloqueantes.length} congelamento(s) bloqueante(s)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <PlugZap className="h-3.5 w-3.5" aria-hidden />
          {dados.projectedOutages.length} interrupção(ões) projetada(s)
        </span>
        {analisando ? (
          <span className="inline-flex items-center gap-1.5" role="status">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            analisando conflitos…
          </span>
        ) : null}
      </div>
    </div>
  );
}

// ── faixa de congelamentos ─────────────────────────────────────────────────

/**
 * Hachura diagonal, e não cor chapada.
 *
 * Um fundo vermelho translúcido se confunde com "linha selecionada" ou "erro
 * de validação" na primeira olhada. A hachura não se confunde com nada nesta
 * aplicação — é o padrão de "área interditada", e é exatamente isso que um
 * congelamento é.
 */
const HACHURA_BLOQUEANTE =
  'repeating-linear-gradient(45deg, hsl(var(--destructive) / 0.20) 0 5px, transparent 5px 10px)';
const HACHURA_INFORMATIVA =
  'repeating-linear-gradient(45deg, hsl(var(--warning) / 0.22) 0 5px, transparent 5px 10px)';

function FaixaDeCongelamentos({
  periodo,
  congelamentos,
}: {
  periodo: Periodo;
  congelamentos: readonly Congelamento[];
}) {
  const vigentes = congelamentos.filter((c) => c.active);
  if (vigentes.length === 0) return null;

  return (
    <Card>
      <CardHeader
        title="Congelamentos no período"
        description="Bloqueante recusa o agendamento e só cede a uma exceção aprovada. Informativo avisa e deixa passar."
      />
      <CardBody className="space-y-2">
        {vigentes.map((congelamento) => {
          const posicao = posicaoNaFaixa(intervaloDe(congelamento), periodo);
          const bloqueante = congelamento.enforcement === 'BLOCKING';
          return (
            <div key={congelamento.id} className="space-y-1">
              <div className="flex flex-wrap items-baseline gap-2 text-sm">
                <Snowflake
                  className={cn(
                    'h-3.5 w-3.5',
                    bloqueante ? 'text-destructive' : 'text-warning',
                  )}
                  aria-hidden
                />
                <span className="font-medium">{congelamento.name}</span>
                <span className="text-xs text-muted-foreground">{congelamento.reason}</span>
                <span className="ml-auto text-xs tabular-nums text-muted-foreground">
                  {formatarDataHora(congelamento.startsAt)} → {formatarDataHora(congelamento.endsAt)}
                </span>
              </div>
              <div className="h-4 w-full overflow-hidden rounded-sm border border-border bg-muted/40">
                <div
                  className={cn(
                    'h-full border-y-2',
                    bloqueante ? 'border-destructive' : 'border-warning',
                  )}
                  style={{
                    marginLeft: `${posicao.esquerda}%`,
                    width: `${posicao.largura}%`,
                    backgroundImage: bloqueante ? HACHURA_BLOQUEANTE : HACHURA_INFORMATIVA,
                  }}
                  title={`${congelamento.name} — ${
                    bloqueante ? 'bloqueia o agendamento' : 'apenas informativo'
                  }`}
                />
              </div>
            </div>
          );
        })}
      </CardBody>
    </Card>
  );
}

// ── grade do mês ───────────────────────────────────────────────────────────

const DIAS_DA_SEMANA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'] as const;

function Grade({
  referencia,
  janelas,
  congelamentos,
  mudancasPorJanela,
  conflitosPorMudanca,
  travadas,
}: {
  referencia: Date;
  janelas: readonly JanelaListada[];
  congelamentos: readonly Congelamento[];
  mudancasPorJanela: Map<string, JanelaDetalhada['changes']>;
  conflitosPorMudanca: Map<string, number>;
  travadas: ReadonlySet<string>;
}) {
  const semanas = useMemo(() => semanasDoMes(referencia), [referencia]);

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card shadow-card">
      <div className="grid grid-cols-7 border-b border-border bg-muted/40">
        {DIAS_DA_SEMANA.map((dia) => (
          <div
            key={dia}
            className="px-2 py-1.5 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground"
          >
            {dia}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {semanas.flat().map((dia) => (
          <Celula
            key={dia.toISOString()}
            dia={dia}
            referencia={referencia}
            janelas={noDia(dia, janelas)}
            congelamento={congelamentoDoDia(dia, congelamentos)}
            congelamentosDoDia={congelamentosDoDia(dia, congelamentos)}
            mudancasPorJanela={mudancasPorJanela}
            conflitosPorMudanca={conflitosPorMudanca}
            travadas={travadas}
          />
        ))}
      </div>
    </div>
  );
}

function Celula({
  dia,
  referencia,
  janelas,
  congelamento,
  congelamentosDoDia: alcancando,
  mudancasPorJanela,
  conflitosPorMudanca,
  travadas,
}: {
  dia: Date;
  referencia: Date;
  janelas: JanelaListada[];
  congelamento: 'BLOCKING' | 'ADVISORY' | null;
  congelamentosDoDia: Congelamento[];
  mudancasPorJanela: Map<string, JanelaDetalhada['changes']>;
  conflitosPorMudanca: Map<string, number>;
  travadas: ReadonlySet<string>;
}) {
  const doMes = mesmoMes(dia, referencia);
  const hoje = ehHoje(dia);
  const nomes = alcancando.map((c) => c.name).join(' · ');

  return (
    <div
      className={cn(
        'min-h-28 space-y-1 border-b border-r border-border p-1.5 last:border-r-0',
        !doMes && 'bg-muted/30',
      )}
      style={
        congelamento === null
          ? undefined
          : {
              backgroundImage:
                congelamento === 'BLOCKING' ? HACHURA_BLOQUEANTE : HACHURA_INFORMATIVA,
            }
      }
      // O congelamento precisa ser legível para quem não vê a hachura: o
      // título dá o nome, e o ícone abaixo dá a palavra.
      title={nomes === '' ? undefined : `Congelamento: ${nomes}`}
    >
      <div className="flex items-center justify-between gap-1">
        <span
          className={cn(
            'grid h-6 w-6 place-items-center rounded-full text-xs font-semibold tabular-nums',
            hoje && 'bg-primary text-primary-foreground',
            !hoje && (doMes ? 'text-foreground' : 'text-muted-foreground'),
          )}
        >
          {dia.getDate()}
        </span>
        {congelamento !== null ? (
          <span
            className={cn(
              'inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] font-bold uppercase tracking-wide',
              congelamento === 'BLOCKING'
                ? 'bg-destructive text-destructive-foreground'
                : 'bg-warning text-warning-foreground',
            )}
          >
            <Snowflake className="h-2.5 w-2.5" aria-hidden />
            {congelamento === 'BLOCKING' ? 'congelado' : 'aviso'}
          </span>
        ) : null}
      </div>

      {janelas.map((janela) => (
        <ChipDeJanela
          key={janela.id}
          janela={janela}
          mudancas={mudancasPorJanela.get(janela.id) ?? []}
          conflitosPorMudanca={conflitosPorMudanca}
          travadas={travadas}
        />
      ))}
    </div>
  );
}

function ChipDeJanela({
  janela,
  mudancas,
  conflitosPorMudanca,
  travadas,
}: {
  janela: JanelaListada;
  mudancas: JanelaDetalhada['changes'];
  conflitosPorMudanca: Map<string, number>;
  travadas: ReadonlySet<string>;
}) {
  const comConflito = mudancas.filter((m) => (conflitosPorMudanca.get(m.ticketId) ?? 0) > 0).length;
  const travadasAqui = mudancas.filter((m) => travadas.has(m.ticketId)).length;
  const cancelada = janela.status === 'CANCELLED';

  return (
    <div
      className={cn(
        'rounded-md border px-1.5 py-1 text-[11px] leading-tight',
        cancelada
          ? 'border-dashed border-border bg-muted/50 text-muted-foreground'
          : 'border-primary/25 bg-primary/10',
      )}
    >
      <p className={cn('truncate font-semibold', cancelada && 'line-through')} title={janela.name}>
        {janela.name}
      </p>
      <p className="tabular-nums text-muted-foreground">
        {rotuloDeHorario(janela.startsAt, janela.endsAt)}
      </p>

      {mudancas.length > 0 ? (
        <ul className="mt-1 space-y-0.5">
          {mudancas.map((membro) => (
            <li key={membro.ticketId} className="truncate">
              <LinkDaMudanca
                ticketId={membro.ticketId}
                title={membro.ticket.subject}
                className="font-mono text-[10px] font-semibold text-primary hover:underline"
              >
                {membro.ticket.number}
              </LinkDaMudanca>
              {/* Cadeado e triângulo dizem coisas diferentes: o primeiro é
                  congelamento, que impede; o segundo é colisão, que o comitê
                  decide. Um ícone só para as duas apagaria a distinção. */}
              {travadas.has(membro.ticketId) ? (
                <Lock className="ml-1 inline h-3 w-3 text-destructive" aria-label="bloqueada" />
              ) : (conflitosPorMudanca.get(membro.ticketId) ?? 0) > 0 ? (
                <TriangleAlert
                  className="ml-1 inline h-3 w-3 text-warning"
                  aria-label="tem conflito"
                />
              ) : null}
            </li>
          ))}
        </ul>
      ) : janela._count.changes > 0 ? (
        <p className="text-muted-foreground">{janela._count.changes} mudança(s)</p>
      ) : null}

      {travadasAqui > 0 ? (
        <p className="mt-0.5 font-semibold text-destructive">{travadasAqui} bloqueada(s)</p>
      ) : null}
      {comConflito - travadasAqui > 0 ? (
        <p className="mt-0.5 font-semibold text-warning">{comConflito - travadasAqui} em conflito</p>
      ) : null}
    </div>
  );
}

// ── conflitos ──────────────────────────────────────────────────────────────

/**
 * A colisão apresentada como decisão, e não como lista de avisos.
 *
 * Cada linha nomeia **as duas mudanças** e **o recurso disputado**, que é
 * exatamente o que o comitê precisa para resolver: adiar uma, agrupá-las numa
 * versão de entrega, ou aceitar e seguir. A janela e a fatia de tempo em comum
 * vêm junto porque a pergunta seguinte é sempre "sobrepõe quanto?".
 */
function PainelDeConflitos({
  panorama,
  analisando,
}: {
  panorama: PanoramaDeConflitos;
  analisando: boolean;
}) {
  const total = panorama.colisoes.length + panorama.bloqueios.length;

  return (
    <Card>
      <CardHeader
        title="Conflitos no período"
        description={
          <>
            Colisão entre mudanças <strong>não impede</strong> o agendamento — duas mudanças no
            mesmo servidor na mesma noite é o que uma versão de entrega faz de propósito. Ela
            aparece aqui porque a decisão é do comitê. Congelamento, ao contrário, recusa.
          </>
        }
      />
      <CardBody className="space-y-3">
        {analisando && total === 0 ? (
          <SkeletonRegion label="Analisando conflitos" className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </SkeletonRegion>
        ) : null}

        {!analisando && total === 0 ? (
          <EmptyState
            icon={<Info className="h-6 w-6" aria-hidden />}
            title="Nenhuma colisão detectada neste período"
            description="Nenhuma mudança agendada disputa item de configuração ou serviço com outra, e nenhuma caiu dentro de um congelamento."
          />
        ) : null}

        {panorama.bloqueios.map((bloqueio) => (
          <AvisoDeConflito
            key={bloqueio.chave}
            gravidade={bloqueio.gravidade}
            titulo={
              <span className="inline-flex flex-wrap items-center gap-2">
                <LinkDaMudanca
                  ticketId={bloqueio.mudanca.ticketId}
                  className="font-mono underline-offset-2 hover:underline"
                >
                  {bloqueio.mudanca.numero}
                </LinkDaMudanca>
                <span className="font-normal">{bloqueio.mudanca.assunto}</span>
              </span>
            }
            acao={
              bloqueio.gravidade === 'BLOQUEANTE' ? (
                <LinkDaMudanca
                  ticketId={bloqueio.mudanca.ticketId}
                  className="inline-flex h-8 items-center rounded-md border border-destructive/40 px-3 text-xs font-semibold text-destructive transition-colors hover:bg-destructive/10"
                >
                  Pedir exceção
                </LinkDaMudanca>
              ) : undefined
            }
          >
            <p>{bloqueio.mensagem}</p>
            {bloqueio.janelaNome ? (
              <p className="text-xs opacity-80">Janela: {bloqueio.janelaNome}</p>
            ) : null}
          </AvisoDeConflito>
        ))}

        {panorama.colisoes.map((colisao) => (
          <AvisoDeConflito
            key={colisao.chave}
            gravidade={colisao.gravidade}
            titulo={
              <span className="inline-flex flex-wrap items-center gap-2">
                <LinkDaMudanca
                  ticketId={colisao.a.ticketId}
                  className="font-mono underline-offset-2 hover:underline"
                >
                  {colisao.a.numero}
                </LinkDaMudanca>
                <span aria-hidden>×</span>
                <LinkDaMudanca
                  ticketId={colisao.b.ticketId}
                  className="font-mono underline-offset-2 hover:underline"
                >
                  {colisao.b.numero}
                </LinkDaMudanca>
                <span className="font-normal">
                  — {ROTULO_DE_TIPO_DE_CONFLITO[colisao.tipo].toLowerCase()}
                </span>
              </span>
            }
          >
            <p className="text-sm">
              {colisao.a.assunto} <span aria-hidden>·</span> {colisao.b.assunto}
            </p>
            <p className="text-xs opacity-80">
              {colisao.recursos.length}{' '}
              {colisao.tipo === 'IC_COMPARTILHADO'
                ? 'item(ns) de configuração em comum'
                : 'serviço(s) em comum'}{' '}
              · sobreposição de {formatarMinutos(
                Math.round((colisao.sobreposicao.fim - colisao.sobreposicao.inicio) / 60_000),
              )}{' '}
              ({formatarDataHora(new Date(colisao.sobreposicao.inicio).toISOString())} →{' '}
              {formatarDataHora(new Date(colisao.sobreposicao.fim).toISOString())})
            </p>
          </AvisoDeConflito>
        ))}
      </CardBody>
    </Card>
  );
}

// ── Interrupção Projetada de Serviço ───────────────────────────────────────

function PainelDePso({
  interrupcoes,
}: {
  interrupcoes: readonly Calendario['projectedOutages'][number][];
}) {
  return (
    <Card>
      <CardHeader
        title="Interrupção Projetada de Serviço (PSO)"
        description="O que vai ficar fora do ar, quando e por quanto tempo. Só entra o serviço que a mudança declara derrubar — mexer sem derrubar não é interrupção, e listar as duas coisas juntas ensinaria o cliente a ignorar o aviso."
      />
      <CardBody>
        {interrupcoes.length === 0 ? (
          <EmptyState
            icon={<PlugZap className="h-6 w-6" aria-hidden />}
            title="Nenhuma queda projetada neste período"
            description="Ou nenhuma mudança agendada derruba serviço, ou a queda ainda não foi declarada na ficha da mudança."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="py-2 pr-3 font-medium">Serviço</th>
                  <th className="py-2 pr-3 font-medium">Janela</th>
                  <th className="py-2 pr-3 font-medium">Quando</th>
                  <th className="py-2 pr-3 font-medium">Parada prevista</th>
                  <th className="py-2 font-medium">Mudança</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {interrupcoes.map((linha) => (
                  <tr key={`${linha.changeTicketId}-${linha.serviceId}`}>
                    <td className="py-2 pr-3 font-medium">{linha.serviceName}</td>
                    <td className="py-2 pr-3">
                      <span className="mr-2">{linha.windowName}</span>
                      <EtiquetaDeJanela situacao={linha.windowStatus} />
                    </td>
                    <td className="py-2 pr-3 tabular-nums text-muted-foreground">
                      {formatarDataHora(linha.startsAt)} ·{' '}
                      {rotuloDeHorario(linha.startsAt, linha.endsAt)}
                    </td>
                    <td className="py-2 pr-3 tabular-nums">
                      {linha.expectedDowntimeMinutes === null ? (
                        <span
                          className="text-muted-foreground"
                          title="A ficha da mudança não declarou quanto tempo o serviço fica fora."
                        >
                          não informada
                        </span>
                      ) : (
                        formatarMinutos(linha.expectedDowntimeMinutes)
                      )}
                    </td>
                    <td className="py-2">
                      <LinkDaMudanca
                        ticketId={linha.changeTicketId}
                        title={linha.changeSubject}
                        className="font-mono text-xs font-semibold text-primary hover:underline"
                      >
                        {linha.changeNumber}
                      </LinkDaMudanca>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

